/**
 * Echo Hunt -- Corpus-tab sort ordering (P5 follow-up).
 *
 * The Corpus tab used to order rows by `indexedAt` descending, which reads as "random" in practice:
 * `indexedAt` reflects when the INDEX PASS last touched a row, not anything about the dream, so an
 * incremental run shuffles whatever it happens to re-index to the top.
 *
 * This module makes the ordering explicit and user-controlled:
 *  - `dreamDate`  -- when the dream happened (`row.date`, i.e. the parent entry's sleep time);
 *  - `indexDate`  -- when the row was last (re-)indexed (`row.indexedAt`);
 *  - `path`       -- the journal path `entryId/segmentId/ordinal`, e.g.
 *                    `-Rl1LVTVSCeJ8Wcgi7M01A/4YRPLcdVQrKhOR8j20zxCg/7`.
 *
 * Two properties matter for a *stable* feel:
 *  1. `path` compares the ORDINAL NUMERICALLY, not as text. Lexicographically `".../10" < ".../2"`,
 *     which would misorder every dream with 10+ bullets.
 *  2. Every comparator falls back through the other keys and finally the row id, so the order is
 *     TOTAL -- no two distinct rows ever compare equal, and the list cannot reorder itself between
 *     renders (which is exactly what "seems randomly ordered" looks like).
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). It takes a structurally minimal
 * row rather than the `EchoCorpusBullet` class, so it is vitest-testable without dragging
 * mobx-graphlink into the Node lane.
 */

export const CorpusSortBy_values = ["dreamDate", "indexDate", "path"] as const;
export type CorpusSortBy = typeof CorpusSortBy_values[number];

export const CorpusSortBy_labels: Record<CorpusSortBy, string> = {
	dreamDate: "dream date",
	indexDate: "index date",
	path: "path",
};

export const CorpusSortBy_descriptions: Record<CorpusSortBy, string> = {
	dreamDate: "chronological by when the dream happened (the parent entry's date)",
	indexDate: "by when the index pass last wrote the row -- useful right after a run",
	path: "by journal location: entryId/segmentId/bulletNumber, in reading order",
};

/** The minimal row shape the comparators need. `EchoCorpusBullet` is assignable to it. */
export type CorpusRowForSort = {
	id: string;
	/** `entryId/segmentId/<ordinal>` (absent on rows written before P5 recorded it) */
	bulletId?: string|n;
	/** the parent dream's date */
	date?: number|n;
	/** when the index pass last wrote this row */
	indexedAt?: number|n;
};

/** Splits a bullet path into its comparable parts. The ordinal is the LAST `/`-separated segment
 * (parsed as a number); everything before it is the head. This tolerates ids that themselves contain
 * slashes, which is why we split from the end rather than by index. */
export function ParseBulletPath(bulletId: string|n): {head: string, ordinal: number} {
	const raw = bulletId ?? "";
	const lastSlash = raw.lastIndexOf("/");
	if (lastSlash == -1) return {head: raw, ordinal: -1};

	const head = raw.substring(0, lastSlash);
	const ordinalText = raw.substring(lastSlash + 1);
	const ordinal = Number(ordinalText);
	return {head, ordinal: Number.isInteger(ordinal) ? ordinal : -1};
}

/** Compares two paths in journal reading order: entry, then segment, then bullet NUMBER. */
export function CompareBulletPath(a: CorpusRowForSort, b: CorpusRowForSort): number {
	const pathA = ParseBulletPath(a.bulletId);
	const pathB = ParseBulletPath(b.bulletId);

	if (pathA.head != pathB.head) return pathA.head < pathB.head ? -1 : 1;
	if (pathA.ordinal != pathB.ordinal) return pathA.ordinal - pathB.ordinal;
	return 0;
}

/** Compares two rows by ONE primary key. Returns 0 when the keys are equal, so the caller can fall
 * back to the tie-breakers (that is what keeps the order total -- see the module comment). */
export function CompareCorpusRowsByPrimary(a: CorpusRowForSort, b: CorpusRowForSort, sortBy: CorpusSortBy): number {
	switch (sortBy) {
		case "dreamDate": {
			// a missing date sorts as the oldest, rather than jumping to the top
			const dateA = a.date ?? -Infinity;
			const dateB = b.date ?? -Infinity;
			if (dateA == dateB) return 0;
			return dateA < dateB ? -1 : 1;
		}
		case "indexDate": {
			const indexA = a.indexedAt ?? -Infinity;
			const indexB = b.indexedAt ?? -Infinity;
			if (indexA == indexB) return 0;
			return indexA < indexB ? -1 : 1;
		}
		case "path":
			return CompareBulletPath(a, b);
	}
}

/** The tie-breaker chain for each primary key. Explicit rather than "the other two in enum order",
 * because the sensible secondary differs per key:
 *  - by date, the secondary is the journal PATH (a night's segments/bullets read forwards);
 *  - by path, the secondary is the dream date (sanity, and it breaks pre-P5 rows with no path).
 * `path` is therefore the universal secondary, which is what makes a date listing readable. */
const TIE_BREAKER_CHAINS: Record<CorpusSortBy, CorpusSortBy[]> = {
	dreamDate: ["path", "indexDate"],
	indexDate: ["path", "dreamDate"],
	path: ["dreamDate", "indexDate"],
};

/** The full comparator: the chosen key, then its tie-breaker chain, then the row id.
 *
 * The tie-breaker chain is not cosmetic. Same-night dreams share a `date`, an index run writes many
 * rows with the same millisecond `indexedAt`, and pre-P5 rows have no `bulletId` at all -- without a
 * total order the list visibly reshuffles on re-render.
 *
 * `descending` reverses ONLY the primary key, not the tie-breakers. That keeps a run of equal-key
 * rows in journal reading order both ways round: "newest dream first" still lists a night's segments
 * and bullets forwards, rather than backwards.
 */
export function CompareCorpusRows(a: CorpusRowForSort, b: CorpusRowForSort, sortBy: CorpusSortBy, descending: boolean): number {
	const primary = CompareCorpusRowsByPrimary(a, b, sortBy);
	if (primary != 0) return descending ? -primary : primary;

	// tie-breakers: always ascending (journal order), so equal-key runs still read naturally
	for (const tieBreaker of TIE_BREAKER_CHAINS[sortBy]) {
		const result = CompareCorpusRowsByPrimary(a, b, tieBreaker);
		if (result != 0) return result;
	}

	// final: the row id, so two rows can never compare equal (id order is arbitrary, so it is
	// reversed along with the primary key -- it only ever breaks truly identical rows apart)
	if (a.id == b.id) return 0;
	return (a.id < b.id ? -1 : 1) * (descending ? -1 : 1);
}

/** Sorts a copy (never mutates the caller's array, which is usually a mobx-observed collection). */
export function SortCorpusRows<T extends CorpusRowForSort>(rows: T[], sortBy: CorpusSortBy, descending: boolean): T[] {
	return [...rows].sort((a, b)=>CompareCorpusRows(a, b, sortBy, descending));
}
